import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Invoice } from '../../api/models/invoice';
import { InvoicePresenceViewer } from '../../api/models/invoice-presence-viewer';
import { NotificationsService } from '../../api/services/notifications.service';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import {
  RealtimeEvent,
  RealtimeNotificationsService,
} from '../../service/realtime-notifications.service';
import { workflowLabel } from '../../service/workflow-descriptors';

interface ActivityBadge {
  workflowId: string;
  initiatorName: string;
  workflowLabel: string;
}

/**
 * Live roster of users on the same invoice plus a transient badge for
 * any workflow currently running against this invoice. Both surfaces
 * react to the same invoice-scoped SSE stream that the page subscribes
 * to in `invoice-page.component`.
 */
@Component({
  selector: 'app-invoice-presence-roster-widget',
  templateUrl: './invoice-presence-roster-widget.component.html',
  styleUrls: ['./invoice-presence-roster-widget.component.scss'],
})
export class InvoicePresenceRosterWidgetComponent implements OnInit, OnDestroy {
  private readonly invoicePeerService = inject(InvoicePeerService);
  private readonly notificationsApi = inject(NotificationsService);
  private readonly realtime = inject(RealtimeNotificationsService);

  public invoice$: Observable<Invoice | null>;

  private readonly viewersSubject = new BehaviorSubject<InvoicePresenceViewer[]>([]);
  public viewers$ = this.viewersSubject.asObservable();

  // Map of workflow_id → badge entry. Multiple workflows could fire
  // concurrently on the same invoice, so the panel renders all active
  // ones rather than the most recent.
  private readonly activitySubject = new BehaviorSubject<ActivityBadge[]>([]);
  public activity$ = this.activitySubject.asObservable();

  private currentInvoiceId: number | null = null;

  private readonly ngDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.invoice$ = this.invoicePeerService.getInvoice();

    this.invoice$.pipe(takeUntil(this.ngDestroy$)).subscribe((invoice) => {
      if (!invoice) {
        return;
      }
      if (invoice.id === this.currentInvoiceId) {
        return;
      }
      this.currentInvoiceId = invoice.id;
      this.viewersSubject.next([]);
      this.activitySubject.next([]);
      this.loadInitialRoster(invoice.id);
    });

    this.realtime.events$
      .pipe(
        takeUntil(this.ngDestroy$),
        filter((event): event is RealtimeEvent => true),
      )
      .subscribe((event) => this.handleEvent(event));
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  private loadInitialRoster(invoiceId: number): void {
    this.notificationsApi
      .getInvoicePresence({ invoiceID: invoiceId })
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe({
        next: (presence) => this.viewersSubject.next(presence.viewers ?? []),
        error: (err) => console.error(`[presence] failed to load roster for invoice ${invoiceId}`, err),
      });
  }

  private handleEvent(event: RealtimeEvent): void {
    if (this.currentInvoiceId == null) {
      return;
    }
    if (event.event_type === 'invoice_presence_changed' && event.invoice_id === this.currentInvoiceId) {
      // pg_notify caps payloads at ~8 KB so the backend may have
      // truncated the inline `viewers`. Re-fetch the full roster via
      // GET (which has no cap) when that flag is set.
      if (event.truncated === true) {
        this.loadInitialRoster(this.currentInvoiceId);
        return;
      }
      this.viewersSubject.next(event.viewers ?? []);
      return;
    }
    if (event.event_type === 'invoice_workflow_activity' && event.invoice_id === this.currentInvoiceId) {
      this.applyActivity(event);
    }
  }

  private applyActivity(event: { workflow_id: string; status: string; workflow_type: string; workflow_label?: string; initiator_name?: string | null }): void {
    const current = this.activitySubject.value;
    const isTerminal = event.status !== 'started';
    if (isTerminal) {
      this.activitySubject.next(current.filter((b) => b.workflowId !== event.workflow_id));
      return;
    }
    if (current.some((b) => b.workflowId === event.workflow_id)) {
      return;
    }
    this.activitySubject.next([
      ...current,
      {
        workflowId: event.workflow_id,
        initiatorName: event.initiator_name ?? 'Someone',
        workflowLabel: event.workflow_label || workflowLabel(event.workflow_type),
      },
    ]);
  }
}
